import { ref } from 'vue'
import axios from 'axios'
import { sendGraphQLQuery } from '@/services/gql-util'
import { Widget, WidgetCategory } from '@/types/Widgets'

export function useWidgets() {
	const widgets = ref<Widget[]>([])
	const categories = ref<WidgetCategory[]>([])
	const loading = ref(false)
	const error = ref<string | null>(null)

	const getWidgets = async (vhost: string): Promise<void> => {
		loading.value = true
		error.value = null

		const query = `
		query ($vhost: Int!) {
			Widgets {
				widgetList(vhost: $vhost) {
					id
					name
					vhost
					type
					description
					thumbUrl
					embedCode
					status
					createdDate
					lastModified
				}
			}
		}
		`

		try {
			const data = await sendGraphQLQuery(query, { vhost: +vhost })
			if (data?.Widgets?.widgetList) {
				widgets.value = [...data.Widgets.widgetList]
			}
		} catch (err: any) {
			console.error('Error fetching widgets:', err)
			error.value = err.message || 'Failed to fetch widgets'
			// Fallback to REST
			try {
				const response = await axios.post('/services/json', {
					method: 'widgets.getWidgets',
					vhostID: vhost,
				})
				if (response.data.status !== false) {
					widgets.value = response.data.result?.data || []
				}
			} catch (restErr) {
				console.error('REST fallback also failed:', restErr)
			}
		} finally {
			loading.value = false
		}
	}

	const getWidgetCategories = async (): Promise<void> => {
		categories.value = [
			{ id: 'gallery', name: 'Gallery', description: 'Image and video galleries', count: 0 },
			{ id: 'player', name: 'Player', description: 'Media players and embeds', count: 0 },
			{ id: 'carousel', name: 'Carousel', description: 'Scrolling content carousels', count: 0 },
			{ id: 'grid', name: 'Grid', description: 'Content grid layouts', count: 0 },
			{ id: 'feed', name: 'Feed', description: 'Content feeds and streams', count: 0 },
			{ id: 'custom', name: 'Custom', description: 'Custom widget types', count: 0 },
		]
	}

	const createWidget = async (vhost: string, widget: Partial<Widget>): Promise<number | null> => {
		const mutation = `
		mutation ($vhost: Int!, $name: String!, $type: String!, $description: String) {
			WidgetMutations {
				createWidget(vhost: $vhost, name: $name, type: $type, description: $description)
			}
		}
		`

		try {
			const data = await sendGraphQLQuery(mutation, {
				vhost: +vhost,
				name: widget.name,
				type: widget.type,
				description: widget.description,
			})
			return data?.WidgetMutations?.createWidget || null
		} catch (err) {
			console.error('Error creating widget:', err)
			return null
		}
	}

	const deleteWidget = async (widgetId: number): Promise<boolean> => {
		const mutation = `
		mutation ($widgetId: Int!) {
			WidgetMutations {
				deleteWidget(widgetId: $widgetId)
			}
		}
		`

		try {
			await sendGraphQLQuery(mutation, { widgetId })
			return true
		} catch (err) {
			console.error('Error deleting widget:', err)
			return false
		}
	}

	const getEmbedCode = async (widgetId: number): Promise<string> => {
		try {
			const response = await axios.post('/services/json', {
				method: 'widgets.getEmbedCode',
				widgetID: widgetId,
			})
			return response.data.result || ''
		} catch (err) {
			console.error('Error getting embed code:', err)
			return ''
		}
	}

	return {
		widgets,
		categories,
		loading,
		error,
		getWidgets,
		getWidgetCategories,
		createWidget,
		deleteWidget,
		getEmbedCode,
	}
}
